import { useState } from 'react';
import { toast } from 'sonner';

import { Icons } from '@/assets/icons/icons';
import { electronApis } from '@/features/electron-api';

import { Button } from '../ui/Button';
import { Dialog } from '../ui/dialog';
import { IconButton } from '../ui/IconButton';

import { type ModalFC } from './modalsManager';

interface DeviceCodeSignInModalProps {
  url: string;
  code: string;
  slug: string;
}

export const DeviceCodeSignInModal: ModalFC<DeviceCodeSignInModalProps> = ({
  open,
  onOpenChange,
  url,
  code,
  slug,
}) => {
  const [copied, setCopied] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy code');
    }
  };

  const handleCancel = async () => {
    setCancelling(true);
    try {
      await electronApis.axonMcp.respawnServer(slug);
      onOpenChange(false);
    } catch {
      toast.error('Failed to cancel sign-in. Please try again.');
    } finally {
      setCancelling(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content className="max-w-lg">
        <Dialog.Header>
          <Dialog.Title>Microsoft sign-in required</Dialog.Title>
          <Dialog.Description>
            Open the sign-in page below and enter this code to connect your
            Outlook account. The code expires in a few minutes.
          </Dialog.Description>
        </Dialog.Header>

        <span className="flex items-center gap-2 rounded-md border border-border-strong bg-surface py-2 pl-3 pr-2">
          <span className="flex-1 text-center font-mono text-lg tracking-widest">
            {code}
          </span>
          <IconButton
            icon={copied ? Icons.check : Icons.copy}
            size="sm"
            variant="ghost"
            tooltip={copied ? 'Copied' : 'Copy code'}
            onClick={handleCopyCode}
          />
        </span>

        <Dialog.Footer className="mt-4 gap-1">
          <Button
            size="sm"
            variant="outline"
            isLoading={cancelling}
            onClick={() => void handleCancel()}
          >
            Cancel sign-in
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => void electronApis.system.openExternal(url)}
          >
            Open sign-in page
          </Button>
          <Button size="sm" onClick={() => onOpenChange(false)}>
            I&rsquo;ve signed in
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
};
